import { useEffect, useState } from "react";
import MultiSectionDetailsCard from "@/components/MultiSectionDetailsCard";
import { useSnackbar } from "notistack";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { useErrorBanner } from "@/providers/ErrorBannerProvider";
import { FacilityDetailsContactQueryResponse } from "@/features/facility/queries";
import NameEditForm from "./forms/NameEditForm";
import PhoneEditForm from "./forms/PhoneEditForm";
import OfficialEditForm from "./forms/OfficialEditForm";
import CourseDetailsEditForm from "./forms/CourseDetailsEditForm";
import AddressEditForm from "./forms/AddressEditForm";
import SectionAffiliationEditForm from "./forms/SectionAffiliationEditForm";
import ParentCompanyEditForm from "./forms/ParentCompanyEditForm";
import PersonnelEditForm from "./forms/PersonnelEditForm";
import { renderEnumDescription } from "@/lib/enumHelpers";
import { ensureProtocol } from "@/lib/urlUtils";
import { ExternalLink } from "@/components/withLink";
import { formattedAddress } from "@/lib/addressHelpers";
import { Link } from "@/router";
import { renderName } from "@/lib/personHelpers";
import SectionHeading from "@/components/SectionHeading";
import { isThreeDigitSectionId } from "@/lib/sectionLegacyId";

function DetailsColumn({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col gap-y-10">{children}</div>;
}

export function ContactDetails({
  data,
  facilityId
}: {
  data: FacilityDetailsContactQueryResponse;
  facilityId: string;
}) {
  const canEdit = useHasCapabilityBool({ domain: DOMAIN.FACILITIES, permission: PERMISSION.manageFacilities });
  const { setError } = useErrorBanner();
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const clearEditingSection = () => setEditingSection(null);
  const [currentData, setCurrentData] = useState<FacilityDetailsContactQueryResponse>(data);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    setCurrentData(data);
  }, [data]);

  const onSuccess = (updatedFacility: Partial<FacilityDetailsContactQueryResponse["facility"]>) => {
    clearEditingSection();
    setCurrentData((prevData) => ({
      ...prevData,
      facility: {
        ...prevData.facility,
        ...updatedFacility
      }
    }));
    enqueueSnackbar("Facility details updated successfully");
  };

  const onError = (error: string) => {
    clearEditingSection();
    setError(error);
  };

  const { facility } = currentData;

  // Helper function to get general manager name for forms
  const getGeneralManagerName = () => {
    if (facility.generalManager?.__typename === "BackofficePerson") {
      return facility.generalManager.displayName;
    }
    if (facility.generalManager?.__typename === "BackofficeLegacyFacilityGeneralManager") {
      return facility.generalManager.name;
    }
    return null;
  };

  return (
    <>
      <SectionHeading heading="Contact Information" body="View contact information and other facility details below" />
      <div className="flex flex-col-reverse md:flex-row gap-x-10 gap-y-10" data-testid="facility-contact-details">
        <div id="contact-details-column-1" className="basis-1/3">
          <DetailsColumn>
            <MultiSectionDetailsCard
              editingSection={editingSection}
              setEditingSection={setEditingSection}
              sections={[
                {
                  heading: "Name",
                  options: [
                    {
                      iconName: "store" as const,
                      headline: facility.name,
                      overline: "Facility Name",
                      copyable: !!facility.name,
                      copyText: facility.name ?? undefined
                    }
                  ],
                  ...(canEdit && {
                    renderEdit: () => (
                      <NameEditForm
                        facilityId={facilityId}
                        name={facility.name}
                        onCancel={clearEditingSection}
                        onSuccess={onSuccess}
                        onError={onError}
                      />
                    )
                  })
                }
              ]}
            />

            <MultiSectionDetailsCard
              editingSection={editingSection}
              setEditingSection={setEditingSection}
              sections={[
                {
                  heading: "Official",
                  options: [
                    {
                      iconName: "location_on" as const,
                      headline: renderEnumDescription(facility.facilityType, currentData.facilityTypeOptions),
                      overline: "Facility Type"
                    },
                    ...(isThreeDigitSectionId(facility.legacyId)
                      ? [
                          {
                            iconName: "badge" as const,
                            headline: facility.legacyId,
                            overline: "Facility ID",
                            copyable: true,
                            copyText: facility.legacyId ?? undefined,
                            copyFieldLabel: "Facility ID"
                          }
                        ]
                      : []),
                    {
                      iconName: "door_open" as const,
                      headline: renderEnumDescription(facility.operatingStatus, currentData.operatingStatusOptions),
                      overline: "Status"
                    },
                    {
                      iconName: "link" as const,
                      headline:
                        facility.website != null ? (
                          <ExternalLink href={ensureProtocol(facility.website)}>{facility.website}</ExternalLink>
                        ) : null,
                      overline: "Website"
                    }
                  ],
                  ...(canEdit && {
                    renderEdit: () => (
                      <OfficialEditForm
                        facilityId={facilityId}
                        facilityType={facility.facilityType}
                        operatingStatus={facility.operatingStatus}
                        website={facility.website}
                        facilityTypeOptions={currentData.facilityTypeOptions}
                        operatingStatusOptions={currentData.operatingStatusOptions}
                        onCancel={clearEditingSection}
                        onSuccess={onSuccess}
                        onError={onError}
                      />
                    )
                  })
                },
                {
                  heading: "Course Details",
                  options: [
                    {
                      iconName: "sports_golf" as const,
                      headline: renderEnumDescription(facility.courseType, currentData.courseTypeOptions),
                      overline: "Course Type"
                    },
                    {
                      iconName: "store" as const,
                      headline: renderEnumDescription(facility.operationType, currentData.operationTypeOptions),
                      overline: "Operation Type"
                    },
                    {
                      iconName: "calendar_today" as const,
                      headline: (() => {
                        if (facility.lengthOfSeasonInMonths != null && facility.lengthOfSeasonInMonths > 0) {
                          if (facility.lengthOfSeasonInMonths === 12) return "Year-Round";
                          return `Seasonal (${facility.lengthOfSeasonInMonths} months/year)`;
                        }
                        return null;
                      })(),
                      overline: "Operating Calendar"
                    },
                    {
                      iconName: "golf_course" as const,
                      headline: facility.holesInRegulation != null ? `${facility.holesInRegulation}` : "",
                      overline: "Total Regulation Holes"
                    },
                    {
                      iconName: "cleaning_bucket" as const,
                      headline:
                        facility.practiceRangeAvailable != null ? (facility.practiceRangeAvailable ? "Yes" : "No") : "",
                      overline: "Practice Range Available"
                    },
                    {
                      iconName: "strategy" as const,
                      headline: facility.simulatorAvailable != null ? (facility.simulatorAvailable ? "Yes" : "No") : "",
                      overline: "Simulator Available"
                    },
                    {
                      iconName: "search_hands_free" as const,
                      headline: facility.golfCartBrand,
                      overline: "Golf Cart Brand"
                    },
                    {
                      iconName: "group" as const,
                      overline: "Total Membership",
                      headline: facility.membershipTotal?.toString()
                    },
                    {
                      iconName: "group" as const,
                      overline: "Membership – Men",
                      headline: facility.membershipMen?.toString()
                    },
                    {
                      iconName: "group" as const,
                      overline: "Membership – Women",
                      headline: facility.membershipWomen?.toString()
                    },
                    {
                      iconName: "group" as const,
                      overline: "Membership – Juniors",
                      headline: facility.membershipJunior?.toString()
                    },
                    {
                      iconName: "group" as const,
                      overline: "Membership – Seniors",
                      headline: facility.membershipSenior?.toString()
                    }
                  ],
                  ...(canEdit && {
                    renderEdit: () => (
                      <CourseDetailsEditForm
                        facilityId={facilityId}
                        courseType={facility.courseType}
                        operationType={facility.operationType}
                        lengthOfSeasonInMonths={facility.lengthOfSeasonInMonths}
                        holesInRegulation={facility.holesInRegulation}
                        practiceRangeAvailable={facility.practiceRangeAvailable}
                        simulatorAvailable={facility.simulatorAvailable}
                        golfCartBrand={facility.golfCartBrand}
                        membershipTotal={facility.membershipTotal}
                        membershipMen={facility.membershipMen}
                        membershipWomen={facility.membershipWomen}
                        membershipJunior={facility.membershipJunior}
                        membershipSenior={facility.membershipSenior}
                        courseTypeOptions={currentData.courseTypeOptions}
                        operationTypeOptions={currentData.operationTypeOptions}
                        onCancel={clearEditingSection}
                        onSuccess={onSuccess}
                        onError={onError}
                      />
                    )
                  })
                }
              ]}
            />
          </DetailsColumn>
        </div>
        <div id="contact-details-column-2" className="basis-2/3">
          <DetailsColumn>
            <MultiSectionDetailsCard
              editingSection={editingSection}
              setEditingSection={setEditingSection}
              sections={[
                {
                  heading: "Phone",
                  options: [
                    {
                      iconName: "call" as const,
                      headline: facility.phone?.phoneFormatted,
                      overline: "Business",
                      copyable: !!facility.phone?.phoneFormatted,
                      copyText: facility.phone?.phoneFormatted ?? undefined
                    },
                    {
                      iconName: "fax" as const,
                      headline: facility.fax?.phoneFormatted,
                      overline: "Fax",
                      copyable: !!facility.fax?.phoneFormatted,
                      copyText: facility.fax?.phoneFormatted ?? undefined
                    },
                    {
                      iconName: "fax" as const,
                      headline: facility.golfShopPhone?.phoneFormatted,
                      overline: "Golf Shop",
                      copyable: !!facility.golfShopPhone?.phoneFormatted,
                      copyText: facility.golfShopPhone?.phoneFormatted ?? undefined
                    }
                  ],
                  ...(canEdit && {
                    renderEdit: () => (
                      <PhoneEditForm
                        facilityId={facilityId}
                        phone={facility.phone}
                        fax={facility.fax}
                        golfShopPhone={facility.golfShopPhone}
                        onCancel={clearEditingSection}
                        onSuccess={onSuccess}
                        onError={onError}
                      />
                    )
                  })
                }
              ]}
            />

            <MultiSectionDetailsCard
              editingSection={editingSection}
              setEditingSection={setEditingSection}
              sections={[
                {
                  heading: "Address",
                  options: [
                    {
                      iconName: "location_on" as const,
                      headline: facility.mainAddress != null ? formattedAddress(facility.mainAddress) : "",
                      overline: "Main",
                      copyable: facility.mainAddress != null,
                      copyText: facility.mainAddress != null ? formattedAddress(facility.mainAddress) : undefined
                    },
                    {
                      iconName: "location_on" as const,
                      headline: facility.mailingAddress != null ? formattedAddress(facility.mailingAddress) : "",
                      overline: "Mailing",
                      copyable: facility.mailingAddress != null,
                      copyText: facility.mailingAddress != null ? formattedAddress(facility.mailingAddress) : undefined
                    }
                  ],
                  ...(canEdit && {
                    renderEdit: () => (
                      <AddressEditForm
                        facilityId={facilityId}
                        mainAddress={facility.mainAddress}
                        mailingAddress={facility.mailingAddress}
                        onCancel={clearEditingSection}
                        onSuccess={onSuccess}
                        onError={onError}
                      />
                    )
                  })
                },
                {
                  heading: "Section Affiliation",
                  options: [
                    {
                      iconName: "domain" as const,
                      headline: facility.section?.name,
                      overline: "Section Name"
                    },
                    {
                      iconName: "villa" as const,
                      headline: facility.chapter?.name,
                      overline: "Section Chapter Name"
                    }
                  ],
                  ...(canEdit && {
                    renderEdit: () => (
                      <SectionAffiliationEditForm
                        facilityId={facilityId}
                        facilityName={facility.name}
                        section={facility.section}
                        chapter={facility.chapter}
                        onCancel={clearEditingSection}
                        onSuccess={onSuccess}
                        onError={onError}
                      />
                    )
                  })
                }
              ]}
            />
            <MultiSectionDetailsCard
              editingSection={editingSection}
              setEditingSection={setEditingSection}
              sections={[
                {
                  heading: "Parent Company",
                  options: [
                    {
                      iconName: "store" as const,
                      headline: facility.parentCompany?.name,
                      overline: "Parent Company"
                    }
                  ],
                  ...(canEdit && {
                    renderEdit: () => (
                      <ParentCompanyEditForm
                        facilityId={facilityId}
                        parentCompany={facility.parentCompany}
                        onCancel={clearEditingSection}
                        onSuccess={onSuccess}
                        onError={onError}
                      />
                    )
                  })
                }
              ]}
            />
            <MultiSectionDetailsCard
              editingSection={editingSection}
              setEditingSection={setEditingSection}
              sections={[
                {
                  heading: "Personnel",
                  options: [
                    {
                      iconName: "person" as const,
                      headline: (() => {
                        if (facility.generalManager != null) {
                          if (facility.generalManager.__typename === "BackofficePerson") {
                            const { displayName, informalName, preferredLastName, suffixName, firstName } =
                              facility.generalManager;
                            return renderName(
                              displayName,
                              informalName,
                              preferredLastName,
                              suffixName,
                              undefined,
                              firstName
                            );
                          }
                          return facility.generalManager.name;
                        }
                        return null;
                      })(),
                      overline: "General Manager"
                    },
                    {
                      iconName: "supervisor_account" as const,
                      headline: facility?.managementGroup,
                      overline: "Management Group"
                    },
                    {
                      iconName: "shield_person" as const,
                      headline: (() => {
                        if (facility.highestRankedEmployment?.person?.pgaId) {
                          return (
                            <Link
                              to="/people/:pgaId/person/overview"
                              params={{ pgaId: facility.highestRankedEmployment.person.pgaId }}
                              className="underline hover:no-underline"
                            >
                              {facility.highestRankedEmployment.person.displayName}
                            </Link>
                          );
                        }
                        return facility.highestRankedEmployment?.person?.displayName;
                      })(),
                      supportingText: facility.highestRankedEmployment?.title,
                      overline: "Highest Ranking PGA Member",
                      tooltipTitle: "Highest Ranking PGA Member",
                      tooltipSupportingText:
                        "The PGA Member with the highest classification rank currently employed at this facility. Highest ranking Members may be used as the point of contact for certain Association programs."
                    }
                  ],
                  ...(canEdit && {
                    renderEdit: () => (
                      <PersonnelEditForm
                        facilityId={facilityId}
                        generalManagerName={getGeneralManagerName()}
                        managementGroup={facility.managementGroup}
                        onCancel={clearEditingSection}
                        onSuccess={onSuccess}
                        onError={onError}
                      />
                    )
                  })
                }
              ]}
            />
          </DetailsColumn>
        </div>
      </div>
    </>
  );
}
